import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "meamor:cart:v3";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  // items: [{ product_id, quantity, personalize, personalize_note, subscribe }]
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
    } catch (_e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_e) {
      // ignore
    }
  }, [items]);

  const addItem = (product_id, quantity = 1) => {
    setItems((curr) => {
      const existing = curr.find((i) => i.product_id === product_id);
      if (existing) {
        return curr.map((i) =>
          i.product_id === product_id
            ? { ...i, quantity: Math.min(99, i.quantity + quantity) }
            : i
        );
      }
      return [
        ...curr,
        {
          product_id,
          quantity: Math.min(99, quantity),
          personalize: false,
          personalize_note: "",
          recipient_age: null,
        },
      ];
    });
    setIsOpen(true);
  };

  const increment = (product_id) => {
    setItems((curr) =>
      curr.map((i) =>
        i.product_id === product_id
          ? { ...i, quantity: Math.min(99, i.quantity + 1) }
          : i
      )
    );
  };

  const decrement = (product_id) => {
    setItems((curr) =>
      curr
        .map((i) =>
          i.product_id === product_id ? { ...i, quantity: i.quantity - 1 } : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (product_id) => {
    setItems((curr) => curr.filter((i) => i.product_id !== product_id));
  };

  const setPersonalize = (product_id, enabled) => {
    setItems((curr) =>
      curr.map((i) =>
        i.product_id === product_id
          ? {
              ...i,
              personalize: !!enabled,
              personalize_note: enabled ? i.personalize_note || "" : "",
            }
          : i
      )
    );
  };

  const setPersonalizeNote = (product_id, note) => {
    setItems((curr) =>
      curr.map((i) =>
        i.product_id === product_id
          ? { ...i, personalize_note: note.slice(0, 280) }
          : i
      )
    );
  };

  const setRecipientAge = (product_id, age) => {
    const n = age === "" || age === null ? null : Number(age);
    const safe = Number.isFinite(n) && n >= 4 && n <= 15 ? n : null;
    setItems((curr) =>
      curr.map((i) =>
        i.product_id === product_id ? { ...i, recipient_age: safe } : i
      )
    );
  };

  const clear = () => setItems([]);

  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const value = {
    items,
    count,
    isOpen,
    setIsOpen,
    addItem,
    increment,
    decrement,
    removeItem,
    setPersonalize,
    setPersonalizeNote,
    setRecipientAge,
    clear,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
